import { asProblem, ListTable, problemMessage, StatusTag, type RecordValues } from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';

/** A role's *Who holds it* tab (ADR 0116): every staff member given it, and where: the whole tenant, a brand or a store. */
export function RoleHoldersTab({ role }: { role: RecordValues }) {
  const { t } = useTranslation();
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });
  const holders = useQuery({
    queryKey: ['ho', 'role-holders', role.id, paging.page, paging.pageSize],
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/identity/roles/{id}/holders', {
        params: { path: { id: String(role.id) }, query: { page: paging.page, pageSize: paging.pageSize } },
      });
      if (!data) throw error;
      return data;
    },
    placeholderData: keepPreviousData,
  });
  type Holder = NonNullable<typeof holders.data>['items'][number];

  return (
    <ListTable<Holder & { id: string }>
      ariaLabel={t('roleHolders.title')}
      loading={holders.isLoading}
      error={holders.error ? problemMessage(t, asProblem(holders.error)) : undefined}
      onRetry={() => void holders.refetch()}
      rows={(holders.data?.items ?? []).map((h) => ({ ...h, id: h.assignmentId }))}
      columns={[
        { key: 'staff', header: t('roleHolders.staff'), render: (h) => `${h.staffCode} · ${h.displayName}` },
        {
          key: 'status',
          header: t('roleHolders.status'),
          render: (h) => <StatusTag status={h.status}>{t(`md.enums.status.${h.status}` as I18nKey, { defaultValue: h.status })}</StatusTag>,
        },
        {
          key: 'where',
          header: t('roleHolders.where'),
          render: (h) =>
            h.scope === 'store'
              ? (h.storeName ?? '—')
              : h.scope === 'store_group'
                ? (h.storeGroupName ?? '—')
                : h.scope === 'brand'
                  ? (h.brandName ?? '—')
                  : t('roleHolders.tenant'),
        },
      ]}
      empty={{ heading: t('roleHolders.emptyTitle'), description: t('roleHolders.empty') }}
      pagination={{ ...paging, total: Number(holders.data?.total ?? 0), onChange: ({ page, pageSize }) => setPaging({ page, pageSize }) }}
    />
  );
}
